import React, { useState, useEffect, useMemo } from 'react';
import { 
  X, FileText, Calendar, Info, AlertCircle, Loader, Building, Lock, Shield, 
  User, CheckCircle, AlertTriangle, Database, Eye, EyeOff, Save, Edit3, Clock, 
  Bike, Calculator, Settings, Target, DollarSign, Package, Plus, Trash2, Camera, Upload,
  Phone, Mail, MapPin, MessageSquare, Percent, Tag, Gift, Download
} from 'lucide-react';
import { 
  collection, getDocs, query, where, addDoc, updateDoc, doc, serverTimestamp, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import CustomerInformationSection from './CustomerInformationSection';
import RentalDetailsSection from './RentalDetailsSection';
import DepositSelectionSection from './DepositSelectionSection';
import BookingStatusSection from './BookingStatusSection';
// ✅ CHANGED: also import the shared size limit
import DocumentRequirementsSection, { MAX_DOCUMENT_SIZE_BYTES } from './DocumentRequirementsSection';
import RentalAddonsSection from './RentalAddonsSection';
import DiscountSection from './DiscountSection';
import CustomFieldsSection from './CustomFieldsSection';
import PaymentMethodSection from './PaymentMethodSection';

// Custom motorbike icon component
const MotorbikeIcon = ({ size = 16, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M8 5h8l2 4h1a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1a3 3 0 0 1-3 3 3 3 0 0 1-3-3H7a3 3 0 0 1-3 3 3 3 0 0 1-3-3V7a2 2 0 0 1 2-2h5zm0 9a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm8 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
  </svg>
);

// ENHANCED SimplifiedReservationForm Component with MOBILE RESPONSIVENESS
const SimplifiedReservationForm = ({ 
  reservationData, 
  handleInputChange, 
  handleSaveReservation, 
  handleClosePopup, 
  editIndex, 
  motorbikes = [],
  isSaving = false,
  currentUser,
  currentCompany,
  tenantContext,
  dataIsolationActive = false,
  validateCompanyAccess,
  getCompanyContext
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  const [bikeStatus, setBikeStatus] = useState({ isAvailable: true, message: '' });
  const [availableBikes, setAvailableBikes] = useState(motorbikes);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [securityWarnings, setSecurityWarnings] = useState([]);
  const [multiTenantReady, setMultiTenantReady] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  
  // Settings state
  const [reservationSettings, setReservationSettings] = useState({
    rentalCalculationMethod: 'inclusive',
    minimumRentalDays: 1,
    allowSameDayRental: true,
    autoCalculateAmount: true,
    depositOptions: [
      { id: 1, amount: '100', currency: 'USD', symbol: '$', label: '$100', isDefault: true }
    ],
    rentalAddons: [],
    documentRequirements: [],
    deliveryLocations: [],
    enableDeliveryCharges: true,
    showDeliveryChargeInCalculation: true,
    paymentMethods: [
      { id: 1, name: 'Cash', type: 'cash', enabled: true, isDefault: true, description: 'Pay with cash on pickup' },
      { id: 2, name: 'Credit Card', type: 'credit_card', enabled: true, isDefault: false, description: 'Pay with credit card' },
      { id: 3, name: 'Bank Transfer', type: 'bank_transfer', enabled: false, isDefault: false, description: 'Direct bank transfer' }
    ],
    discountSettings: {
      enableDiscounts: true,
      allowCustomDiscounts: true,
      requireDiscountReason: false,
      maxDiscountPercentage: 50,
      maxDiscountAmount: 500,
      // ✅ CHANGED: no built-in sample discounts (Early Bird, Loyalty, etc.)
      predefinedDiscounts: []
    },
    fieldSettings: {
      customerName: { visible: true, required: true, label: 'Customer Name', order: 1, icon: 'User', placeholder: 'Enter customer name', description: 'Full name of the person making the reservation' },
      whatsappNumber: { visible: true, required: false, label: 'WhatsApp Number', order: 2, icon: 'Phone', placeholder: 'Enter WhatsApp number', description: 'WhatsApp contact number for communication' },
      email: { visible: true, required: false, label: 'Email', order: 3, icon: 'Mail', placeholder: 'Enter email address', description: 'Email address for booking confirmations' },
      licenseNumber: { visible: true, required: true, label: 'License Number', order: 4, icon: 'Shield', placeholder: 'Enter license number (any format)', description: 'Valid driving license number - Any format accepted' },
      licenseType: { visible: true, required: true, label: 'License Type', order: 5, icon: 'Shield', placeholder: 'Select license type', description: 'Type of driving license', options: ['Car', 'Motorcycle', 'Both'] },
      pickupDateTime: { visible: true, required: true, label: 'Pickup Date & Time', order: 6, icon: 'Calendar', description: 'When the rental period begins' },
      dropoffDateTime: { visible: true, required: true, label: 'Drop-off Date & Time', order: 7, icon: 'Calendar', description: 'When the rental period ends' },
      pickupLocation: { visible: true, required: false, label: 'Pickup Location', order: 8, icon: 'MapPin', description: 'Where to collect the motorbike' },
      dropoffLocation: { visible: true, required: false, label: 'Drop-off Location', order: 9, icon: 'MapPin', description: 'Where to return the motorbike' },
      motorbikeNumber: { visible: true, required: true, label: 'Motorbike', order: 10, icon: 'Bike', description: 'Select the motorbike for rental' },
      availabilityStatus: { 
        visible: true, required: true, label: 'Availability Status', order: 11, icon: 'CheckCircle', 
        description: 'Current status of the reservation', allowedValues: ['booked', 'pending', 'cancel'] 
      },
      note: { visible: true, required: false, label: 'Note', order: 12, icon: 'MessageSquare', placeholder: 'Enter any notes', description: 'Additional information about the reservation', maxLength: 500 }
    },
    formBehavior: {
      compactMode: false,
      groupRelatedFields: true,
      showFieldDescriptions: true,
      showFieldIcons: true,
      enableInlineValidation: true,
      showCharacterCounts: true
    },
    validationRules: {
      whatsappNumberFormat: 'international',
      emailValidation: 'strict',
      customerNameMinLength: 2,
      noteMaxLength: 500,
      requirePhoneOrEmail: false
    },
    defaultPickupLocation: 'SLBR',
    defaultDropoffLocation: 'SLBR',
    requireCustomerEmail: false,
    requireLicenseNumber: true,
    showCalculationDetails: true
  });
  
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(null);
  
  // Addon state
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [addonCosts, setAddonCosts] = useState(0);
  const [originalAddons, setOriginalAddons] = useState([]);
  const [addonChanges, setAddonChanges] = useState(false);
  
  // Document Requirements state
  const [documentUploads, setDocumentUploads] = useState({});
  const [documentValidation, setDocumentValidation] = useState({});
  const [originalDocuments, setOriginalDocuments] = useState({});
  const [documentChanges, setDocumentChanges] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  
  // Camera capture state
  const [cameraStream, setCameraStream] = useState(null);
  const [activeCameraDoc, setActiveCameraDoc] = useState(null);
  const [cameraSupported, setCameraSupported] = useState(false);
  const [capturedImages, setCapturedImages] = useState({});
  
  // Calculated rental amount state
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [calculationDetails, setCalculationDetails] = useState({ 
    days: 0, 
    dailyRate: 0, 
    selectedBike: null,
    method: 'inclusive',
    breakdown: {}
  });

  // Form validation state
  const [fieldValidation, setFieldValidation] = useState({});
  const [formErrors, setFormErrors] = useState([]);

  // Discount state management
  const [discountState, setDiscountState] = useState({
    selectedDiscount: null,
    customDiscountType: 'percentage',
    customDiscountValue: '',
    discountReason: '',
    discountAmount: 0,
    originalDiscount: null,
    discountChanges: false
  });

  // Custom fields state management
  const [customFieldChanges, setCustomFieldChanges] = useState({});
  const [originalCustomFields, setOriginalCustomFields] = useState({});

  // ============================================================================
  // COMPANY CONTEXT AND COLLECTIONS
  // ============================================================================
  
  const companyId = getCompanyContext?.() || currentCompany?.id;
  
  const getCompanyCollectionPath = (collection) => {
    if (!companyId) {
      console.error('No company context available for collection:', collection);
      return null;
    }
    return `companies/${companyId}/${collection}`;
  };

  const reservationsCollection = useMemo(() => {
    const path = getCompanyCollectionPath('reservations');
    return path ? collection(db, path) : null;
  }, [getCompanyContext, currentCompany]);

  const settingsDocRef = useMemo(() => {
    if (!companyId) return null;
    return doc(db, `companies/${companyId}/settings`, 'reservations');
  }, [companyId]);

  // ============================================================================
  // DELIVERY LOCATIONS HELPER FUNCTIONS
  // ============================================================================
  
  const getEnabledLocations = () => {
    const locations = Array.isArray(reservationSettings.deliveryLocations) 
      ? reservationSettings.deliveryLocations 
      : [];
    return locations.filter(loc => loc && loc.enabled === true);
  };

  const getPickupLocations = () => {
    const enabledLocations = getEnabledLocations();
    return enabledLocations.filter(loc => 
      loc.category === 'pickup' || loc.category === 'both'
    );
  };

  const getDropoffLocations = () => {
    const enabledLocations = getEnabledLocations();
    return enabledLocations.filter(loc => 
      loc.category === 'dropoff' || loc.category === 'both'
    );
  };

  const getLocationByName = (locationName) => {
    if (!locationName) return null;
    const locations = Array.isArray(reservationSettings.deliveryLocations) 
      ? reservationSettings.deliveryLocations 
      : [];
    return locations.find(loc => loc.name === locationName);
  };

  const calculateDeliveryCharges = () => {
    if (!reservationSettings.enableDeliveryCharges) return 0;
    
    let totalCharge = 0;
    
    if (reservationData.pickupLocation) {
      const pickupLoc = getLocationByName(reservationData.pickupLocation);
      if (pickupLoc) {
        totalCharge += parseFloat(pickupLoc.deliveryCharge) || 0;
      }
    }
    
    if (reservationData.dropoffLocation) {
      const dropoffLoc = getLocationByName(reservationData.dropoffLocation);
      if (dropoffLoc) {
        totalCharge += parseFloat(dropoffLoc.deliveryCharge) || 0;
      }
    }
    
    return totalCharge;
  };

  // ============================================================================
  // FORM FIELD HELPERS
  // ============================================================================
  
  const getVisibleFields = () => {
    const fieldSettings = reservationSettings.fieldSettings || {};
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => fieldConfig && fieldConfig.visible)
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  const validateField = (fieldKey, value, fieldConfig) => {
    const errors = [];
    
    if (fieldConfig.required && (!value || value.trim() === '')) {
      errors.push(`${fieldConfig.label} is required`);
    }
    
    if (value && value.trim()) {
      switch (fieldKey) {
        case 'email':
          if (reservationSettings.validationRules?.emailValidation === 'strict') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              errors.push('Please enter a valid email address');
            }
          }
          break;
          
        case 'whatsappNumber':
          if (fieldConfig.validationPattern) {
            const regex = new RegExp(fieldConfig.validationPattern);
            if (!regex.test(value)) {
              errors.push('Please enter a valid WhatsApp number');
            }
          }
          break;
          
        case 'customerName':
          if (value.length < (reservationSettings.validationRules?.customerNameMinLength || 2)) {
            errors.push(`Customer name must be at least ${reservationSettings.validationRules?.customerNameMinLength || 2} characters`);
          }
          break;

        case 'licenseNumber':
          // NO VALIDATION - Accept any format
          break;

        case 'licenseType':
          // NO VALIDATION - Accept any license type
          break;
          
        case 'note':
          const maxLength = fieldConfig.maxLength || reservationSettings.validationRules?.noteMaxLength || 500;
          if (value.length > maxLength) {
            errors.push(`Note cannot exceed ${maxLength} characters`);
          }
          break;
      }
    }
    
    return errors;
  };

  // ============================================================================
  // ADDON HELPERS
  // ============================================================================
  
  const getEnabledAddons = () => {
    return (reservationSettings.rentalAddons || []).filter(addon => addon && addon.enabled === true);
  };

  const calculateAddonCosts = (addons, days) => {
    if (!addons || addons.length === 0 || !days) return 0;
    
    return addons.reduce((total, addonId) => {
      const addon = reservationSettings.rentalAddons?.find(a => a.id === addonId && a.enabled);
      if (!addon) return total;
      
      const price = parseFloat(addon.price) || 0;
      if (addon.pricingMethod === 'perDay') {
        return total + (price * days);
      } else {
        return total + price;
      }
    }, 0);
  };

  // ============================================================================
  // DISCOUNT HELPERS
  // ============================================================================
  
  // ✅ CHANGED: no company max limit (was 50% / $500) - the owner decides the discount.
  // Only natural limits remain: max 100%, and never more than the booking amount.
  const calculateDiscountAmount = (baseAmount, discountType, discountValue) => {
    const numValue = parseFloat(discountValue) || 0;
    if (numValue <= 0 || baseAmount <= 0) return 0;
    
    if (discountType === 'percentage') {
      const percentage = Math.min(numValue, 100);
      return (baseAmount * percentage) / 100;
    } else {
      return Math.min(numValue, baseAmount);
    }
  };

  // ✅ CHANGED: only rejects impossible values (negative, or more than 100%)
  const validateDiscountValue = (type, value) => {
    if (!value) return true;
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) return false;
    if (type === 'percentage') return numValue <= 100;
    return true;
  };

  // ============================================================================
  // RENTAL CALCULATION
  // ============================================================================
  
  const calculateRentalAmount = (bikeSelection, pickupDateTime, dropoffDateTime) => {
    if (!bikeSelection || !pickupDateTime || !dropoffDateTime) {
      setCalculatedAmount(0);
      setCalculationDetails({ days: 0, dailyRate: 0, selectedBike: null, method: reservationSettings.rentalCalculationMethod, breakdown: {} });
      return 0;
    }

    const selectedBike = motorbikes.find(bike => 
      bike.regNumber === bikeSelection || 
      bike.id === bikeSelection ||
      (typeof bike === 'string' && bike === bikeSelection)
    );

    if (!selectedBike) {
      setCalculatedAmount(0);
      setCalculationDetails({ days: 0, dailyRate: 0, selectedBike: null, method: reservationSettings.rentalCalculationMethod, breakdown: {} });
      return 0;
    }

    const startDate = new Date(pickupDateTime);
    const endDate = new Date(dropoffDateTime);
    
    let days;
    let breakdown = {};
    
    if (reservationSettings.rentalCalculationMethod === 'inclusive') {
      const startCalendarDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endCalendarDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const timeDiff = endCalendarDate.getTime() - startCalendarDate.getTime();
      days = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
      
      breakdown = {
        startDate: startCalendarDate.toLocaleDateString(),
        endDate: endCalendarDate.toLocaleDateString(),
        calculation: 'Calendar days (inclusive)',
        explanation: `From ${startCalendarDate.toLocaleDateString()} to ${endCalendarDate.toLocaleDateString()} = ${days} day${days !== 1 ? 's' : ''}`
      };
    } else {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (hoursDiff <= 24) {
        days = 1;
      } else {
        days = Math.ceil(hoursDiff / 24);
      }
      
      breakdown = {
        startDate: startDate.toLocaleDateString(),
        endDate: endDate.toLocaleDateString(),
        hoursDiff: Math.round(hoursDiff * 10) / 10,
        calculation: 'Duration-based',
        explanation: `${Math.round(hoursDiff * 10) / 10} hours = ${days} day${days !== 1 ? 's' : ''}`
      };
    }

    if (days < reservationSettings.minimumRentalDays) {
      breakdown.originalDays = days;
      breakdown.minimumApplied = true;
      days = reservationSettings.minimumRentalDays;
    }

    if (!reservationSettings.allowSameDayRental && startDate.toDateString() === endDate.toDateString()) {
      setCalculatedAmount(0);
      setCalculationDetails({ 
        days: 0, 
        dailyRate: 0, 
        selectedBike: null, 
        method: reservationSettings.rentalCalculationMethod,
        breakdown: { error: 'Same-day rentals are not allowed per company settings' }
      });
      return 0;
    }

    const dailyRate = selectedBike.dailyRate || selectedBike.rate || 25;
    const totalAmount = days * dailyRate;

    const calculationData = {
      days,
      dailyRate,
      method: reservationSettings.rentalCalculationMethod,
      selectedBike: {
        regNumber: selectedBike.regNumber,
        model: selectedBike.model,
        dailyRate
      },
      breakdown
    };

    setCalculatedAmount(totalAmount);
    setCalculationDetails(calculationData);

    return totalAmount;
  };

  // ============================================================================
  // LOAD SETTINGS
  // ============================================================================
  
  const loadReservationSettings = async () => {
    if (!settingsDocRef) {
      setSettingsError('No company context available');
      setSettingsLoading(false);
      return;
    }

    try {
      setSettingsLoading(true);
      setSettingsError(null);
      console.log('Loading reservation settings for form...');
      
      const docSnap = await getDoc(settingsDocRef);
      
      if (docSnap.exists()) {
        const loadedSettings = docSnap.data();
        
        // Remove ALL license validation
        if (loadedSettings.fieldSettings?.licenseNumber) {
          delete loadedSettings.fieldSettings.licenseNumber.validationPattern;
          delete loadedSettings.fieldSettings.licenseNumber.pattern;
          delete loadedSettings.fieldSettings.licenseNumber.format;
          delete loadedSettings.fieldSettings.licenseNumber.formatRequired;
          delete loadedSettings.fieldSettings.licenseNumber.regex;
          delete loadedSettings.fieldSettings.licenseNumber.validation;
          
          loadedSettings.fieldSettings.licenseNumber.description = 'Valid driving license number - Any format accepted';
          loadedSettings.fieldSettings.licenseNumber.placeholder = 'Enter license number (any format)';
        }
        
        if (loadedSettings.validationRules) {
          delete loadedSettings.validationRules.licenseNumberPattern;
          delete loadedSettings.validationRules.licenseFormat;
          delete loadedSettings.validationRules.validateLicenseFormat;
          delete loadedSettings.validationRules.licenseValidation;
        }
        
        if (!loadedSettings.fieldSettings) {
          loadedSettings.fieldSettings = reservationSettings.fieldSettings;
        }
        if (!loadedSettings.formBehavior) {
          loadedSettings.formBehavior = reservationSettings.formBehavior;
        }
        if (!loadedSettings.validationRules) {
          loadedSettings.validationRules = reservationSettings.validationRules;
        }
        if (!loadedSettings.discountSettings) {
          loadedSettings.discountSettings = reservationSettings.discountSettings;
        }

        if (loadedSettings.defaultDepositAmount && !loadedSettings.depositOptions) {
          const amount = loadedSettings.defaultDepositAmount.replace(/[^0-9.]/g, '');
          const symbol = loadedSettings.defaultDepositAmount.replace(/[0-9.]/g, '');
          const currency = symbol === '$' ? 'USD' : symbol === '€' ? 'EUR' : symbol === '£' ? 'GBP' : 'USD';
          
          loadedSettings.depositOptions = [
            { 
              id: 1, 
              amount: amount || '100', 
              currency: currency, 
              symbol: symbol || '$', 
              label: loadedSettings.defaultDepositAmount || '$100',
              isDefault: true 
            }
          ];
        }

        if (!loadedSettings.rentalAddons) {
          loadedSettings.rentalAddons = [];
        }

        if (!Array.isArray(loadedSettings.documentRequirements)) {
          loadedSettings.documentRequirements = [];
        }

        if (!Array.isArray(loadedSettings.deliveryLocations)) {
          loadedSettings.deliveryLocations = [];
        }

        if (loadedSettings.enableDeliveryCharges === undefined) {
          loadedSettings.enableDeliveryCharges = true;
        }
        if (loadedSettings.showDeliveryChargeInCalculation === undefined) {
          loadedSettings.showDeliveryChargeInCalculation = true;
        }
        
        setReservationSettings(prev => ({
          ...prev,
          ...loadedSettings
        }));
        
        console.log('✅ Form loaded settings with delivery locations:', loadedSettings.deliveryLocations?.length || 0, 'locations');
      } else {
        console.log('No settings found, using defaults');
      }
    } catch (error) {
      console.error('Error loading reservation settings:', error);
      setSettingsError('Failed to load settings: ' + error.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  // ============================================================================
  // FORM VALIDATION
  // ============================================================================
  
  const validateForm = () => {
    const errors = [];
    const fieldValidationResults = {};
    const visibleFields = getVisibleFields();
    
    visibleFields.forEach(([fieldKey, fieldConfig]) => {
      const value = reservationData[fieldKey] || '';
      const fieldErrors = validateField(fieldKey, value, fieldConfig);
      
      if (fieldErrors.length > 0) {
        fieldValidationResults[fieldKey] = {
          isValid: false,
          errors: fieldErrors
        };
        errors.push(...fieldErrors);
      } else {
        fieldValidationResults[fieldKey] = {
          isValid: true,
          errors: []
        };
      }
    });

    // Cross-field validations
    if (reservationSettings.validationRules?.requirePhoneOrEmail) {
      const hasPhone = reservationData.whatsappNumber?.trim();
      const hasEmail = reservationData.email?.trim();
      if (!hasPhone && !hasEmail) {
        errors.push('Either WhatsApp number or email is required');
      }
    }

    // Date validations
    if (reservationData.pickupDateTime && reservationData.dropoffDateTime) {
      const pickup = new Date(reservationData.pickupDateTime);
      const dropoff = new Date(reservationData.dropoffDateTime);
      
      if (pickup >= dropoff) {
        errors.push('Drop-off time must be after pickup time');
      }
      
      if (!reservationSettings.allowSameDayRental && pickup.toDateString() === dropoff.toDateString()) {
        errors.push('Same-day rentals are not allowed per company settings');
      }
    }

    // Document validation
    const requiredDocs = getRequiredDocuments();
    const missingDocs = requiredDocs.filter(doc => !documentUploads[doc.id]);
    if (missingDocs.length > 0) {
      errors.push(`Missing required documents: ${missingDocs.map(doc => doc.name).join(', ')}`);
    }

    // Discount validation
    if (discountState.customDiscountValue && !validateDiscountValue(discountState.customDiscountType, discountState.customDiscountValue)) {
      // ✅ CHANGED: message for impossible values only
      errors.push(
        discountState.customDiscountType === 'percentage'
          ? 'Discount must be between 0% and 100%'
          : 'Discount amount cannot be negative'
      );
    }

    if (reservationSettings.discountSettings?.requireDiscountReason && (discountState.selectedDiscount || discountState.customDiscountValue) && !discountState.discountReason.trim()) {
      errors.push('Discount reason is required');
    }

    // Custom fields validation
    if (reservationSettings?.fieldSettings) {
      Object.entries(reservationSettings.fieldSettings).forEach(([fieldKey, fieldConfig]) => {
        if (fieldConfig.isCustom && fieldConfig.visible) {
          const value = reservationData[fieldKey] || '';
          const customFieldErrors = [];
          
          if (fieldConfig.required && !value) {
            customFieldErrors.push(`${fieldConfig.label} is required`);
          }
          
          if (value && fieldConfig.validationPattern) {
            try {
              const regex = new RegExp(fieldConfig.validationPattern);
              if (!regex.test(value)) {
                customFieldErrors.push(`${fieldConfig.label} format is invalid`);
              }
            } catch (e) {
              console.error('Invalid regex pattern:', fieldConfig.validationPattern);
            }
          }
          
          if (value && fieldConfig.maxLength && value.length > fieldConfig.maxLength) {
            customFieldErrors.push(`${fieldConfig.label} must be ${fieldConfig.maxLength} characters or less`);
          }
          
          if (value && fieldConfig.fieldType === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
              customFieldErrors.push(`${fieldConfig.label} must be a valid email address`);
            }
          }
          
          if (value && fieldConfig.fieldType === 'url') {
            try {
              new URL(value);
            } catch {
              customFieldErrors.push(`${fieldConfig.label} must be a valid URL`);
            }
          }
          
          if (customFieldErrors.length > 0) {
            fieldValidationResults[fieldKey] = {
              isValid: false,
              errors: customFieldErrors
            };
            errors.push(...customFieldErrors);
          }
        }
      });
    }
    
    setFieldValidation(fieldValidationResults);
    setFormErrors(errors);
    
    return errors.length === 0;
  };

  // ============================================================================
  // INPUT CHANGE HANDLER
  // ============================================================================
  
  const handleEnhancedInputChange = (fieldKeyOrEvent, valueOrNull = null, fieldConfig = null) => {
    let name, value;
    
    if (typeof fieldKeyOrEvent === 'string') {
      name = fieldKeyOrEvent;
      value = valueOrNull;
    } else {
      name = fieldKeyOrEvent.target.name;
      value = fieldKeyOrEvent.target.value;
    }
    
    if (name === 'availabilityStatus' && value === 'cancel') {
      const confirmCancel = window.confirm(
        'Are you sure you want to cancel this reservation? This action cannot be undone.'
      );
      if (!confirmCancel) return;
    }
    
    if (typeof fieldKeyOrEvent === 'string') {
      handleInputChange({ target: { name, value } });
    } else {
      handleInputChange(fieldKeyOrEvent);
    }
    
    if (editIndex !== null && fieldConfig?.isCustom) {
      const hasChanged = originalCustomFields[name] !== value;
      setCustomFieldChanges(prev => ({
        ...prev,
        [name]: hasChanged
      }));
    }
    
    if (reservationSettings.formBehavior?.enableInlineValidation) {
      const configToUse = fieldConfig || reservationSettings.fieldSettings?.[name];
      if (configToUse) {
        const fieldErrors = validateField(name, value, configToUse);
        setFieldValidation(prev => ({
          ...prev,
          [name]: {
            isValid: fieldErrors.length === 0,
            errors: fieldErrors
          }
        }));
      }
    }
  };

  // ============================================================================
  // DOCUMENT HELPERS
  // ============================================================================
  
  const getEnabledDocuments = () => {
    return (reservationSettings.documentRequirements || []).filter(doc => doc && doc.enabled === true);
  };

  const getRequiredDocuments = () => {
    const documents = getEnabledDocuments();
    return documents.filter(doc => doc.required === true);
  };

  const validateDocuments = () => {
    const requiredDocs = getRequiredDocuments();
    const errors = {};
    let hasErrors = false;

    requiredDocs.forEach(doc => {
      if (!documentUploads[doc.id]) {
        errors[doc.id] = {
          isValid: false,
          message: `${doc.name} is required`
        };
        hasErrors = true;
      } else {
        errors[doc.id] = {
          isValid: true,
          message: ''
        };
      }
    });

    setDocumentValidation(errors);
    return !hasErrors;
  };

  // ============================================================================
  // FIREBASE STORAGE - IMAGE COMPRESSION
  // ============================================================================
  
  const compressImage = async (file) => {
    if (!file.type.startsWith('image/')) {
      return file;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          const maxDimension = 1280;
          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            
            const originalKB = (file.size / 1024).toFixed(0);
            const compressedKB = (compressedFile.size / 1024).toFixed(0);
            console.log(`📦 Compressed: ${originalKB}KB → ${compressedKB}KB (${((1 - compressedFile.size/file.size) * 100).toFixed(0)}% smaller)`);
            
            resolve(compressedFile);
          }, 'image/jpeg', 0.6);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  
  // ============================================================================
  // FIREBASE STORAGE - UPLOAD WITH RETRY
  // ============================================================================
  
  const uploadDocumentToStorage = async (file, documentId, documentName, retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      if (!companyId) {
        throw new Error('No company context available for document upload');
      }

      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        console.log(`📸 Compressing image: ${file.name} (${(file.size/1024).toFixed(0)}KB)`);
        fileToUpload = await compressImage(file);
      }

      // ✅ CHANGED: uses the shared limit, and marks the error as "don't retry"
      // (a too-large file will fail every time, so retrying only wastes time)
      if (fileToUpload.size > MAX_DOCUMENT_SIZE_BYTES) {
        const maxMB = (MAX_DOCUMENT_SIZE_BYTES / 1024 / 1024).toFixed(0);
        const fileMB = (fileToUpload.size / 1024 / 1024).toFixed(2);
        const sizeError = new Error(`File too large. Max size is ${maxMB}MB, your file is ${fileMB}MB`);
        sizeError.noRetry = true;
        throw sizeError;
      }

      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${documentId}_${timestamp}_${sanitizedFileName}`;
      
      const storagePath = `companies/${companyId}/reservations/documents/${fileName}`;
      const storageRef = ref(storage, storagePath);
      
      const sizeKB = (fileToUpload.size / 1024).toFixed(2);
      console.log(`⬆️  Attempt ${retryCount + 1}/${maxRetries + 1}: Uploading ${fileName} (${sizeKB}KB)`);
      
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { 
          status: 'uploading', 
          progress: 0, 
          fileName: file.name,
          size: sizeKB + ' KB',
          attempt: retryCount + 1
        }
      }));
      
      const uploadPromise = uploadBytes(storageRef, fileToUpload);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout after 5 minutes')), 300000)
      );
      
      const snapshot = await Promise.race([uploadPromise, timeoutPromise]);
      
      console.log('✓ Upload complete, getting URL...');
      
      const urlPromise = getDownloadURL(snapshot.ref);
      const urlTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('URL timeout')), 30000)
      );
      
      const downloadURL = await Promise.race([urlPromise, urlTimeoutPromise]);
      
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { 
          status: 'complete', 
          progress: 100, 
          fileName: file.name,
          size: sizeKB + ' KB'
        }
      }));
      
      console.log('✅ SUCCESS:', downloadURL);
      
      return {
        downloadURL,
        storagePath,
        fileName: sanitizedFileName,
        fileSize: fileToUpload.size,
        fileType: fileToUpload.type,
        uploadedAt: new Date().toISOString(),
        originalSize: file.size,
        compressed: file.size !== fileToUpload.size,
        retries: retryCount
      };
      
    } catch (error) {
      console.error(`❌ Upload attempt ${retryCount + 1} failed:`, error.message);
      
      // ✅ CHANGED: only retry errors that might succeed next time
      if (retryCount < maxRetries && !error.noRetry) {
        console.log(`🔄 Retrying in 2 seconds... (${retryCount + 1}/${maxRetries})`);
        
        setUploadProgress(prev => ({
          ...prev,
          [documentId]: { 
            status: 'retrying', 
            progress: 0, 
            fileName: file.name,
            attempt: retryCount + 1,
            error: `Retry ${retryCount + 1}/${maxRetries}...`
          }
        }));
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        return uploadDocumentToStorage(file, documentId, documentName, retryCount + 1);
      }
      
      // ✅ CHANGED: report the real number of attempts (1 for size errors)
      const attempts = retryCount + 1;
      const failMessage = error.noRetry
        ? error.message
        : `Failed after ${attempts} attempt${attempts !== 1 ? 's' : ''}: ${error.message}`;
      
      setUploadProgress(prev => ({
        ...prev,
        [documentId]: { 
          status: 'error', 
          progress: 0, 
          error: failMessage, 
          fileName: file.name 
        }
      }));
      
      throw new Error(failMessage);
    }
  };

  // ============================================================================
  // FIREBASE STORAGE - UPLOAD ALL DOCUMENTS
  // ============================================================================
  
  const uploadAllDocuments = async () => {
    const enabledDocs = getEnabledDocuments();
    const uploadedData = {};
    const uploadErrors = [];
    
    setUploadingDocuments(true);
    setUploadProgress({});
    
    try {
      const docsToUpload = enabledDocs.filter(doc => {
        const file = documentUploads[doc.id];
        return file && !file.isExisting;
      });
      
      const existingDocs = enabledDocs.filter(doc => {
        const file = documentUploads[doc.id];
        return file && file.isExisting;
      });
      
      console.log(`📤 Upload plan: ${docsToUpload.length} new, ${existingDocs.length} existing`);
      
      existingDocs.forEach(document => {
        const file = documentUploads[document.id];
        uploadedData[document.id] = {
          documentId: document.id,
          documentName: document.name,
          category: document.category,
          required: document.required,
          fileName: file.name,
          fileSize: file.size,
          downloadURL: file.downloadURL,
          storagePath: file.storagePath,
          uploadedAt: file.uploadedAt,
          status: 'existing',
          isExisting: true
        };
      });
      
      if (docsToUpload.length === 0) {
        console.log('✓ No new documents to upload');
        return uploadedData;
      }
      
      for (const document of docsToUpload) {
        const file = documentUploads[document.id];
        
        try {
          console.log(`\n⬆️  Uploading: ${document.name}`);
          
          const uploadResult = await uploadDocumentToStorage(
            file.file || file,
            document.id,
            document.name
          );
          
          uploadedData[document.id] = {
            documentId: document.id,
            documentName: document.name,
            category: document.category,
            required: document.required,
            fileName: uploadResult.fileName,
            fileSize: uploadResult.fileSize,
            fileType: uploadResult.fileType,
            downloadURL: uploadResult.downloadURL,
            storagePath: uploadResult.storagePath,
            uploadedAt: uploadResult.uploadedAt,
            status: 'uploaded',
            isExisting: false,
            compressed: uploadResult.compressed,
            retries: uploadResult.retries
          };
          
          console.log(`✅ ${document.name} uploaded successfully\n`);
          
        } catch (error) {
          console.error(`❌ Failed: ${document.name}:`, error);
          uploadErrors.push({
            documentName: document.name,
            message: error.message
          });
        }
        
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      if (uploadErrors.length > 0) {
        const errorMessage = uploadErrors.map(e => `• ${e.documentName}: ${e.message}`).join('\n');
        throw new Error(`Some documents failed to upload:\n${errorMessage}`);
      }
      
      console.log(`✅ Upload complete: ${Object.keys(uploadedData).length} documents`);
      return uploadedData;
      
    } catch (error) {
      console.error('❌ Document upload process failed:', error);
      throw error;
    } finally {
      setUploadingDocuments(false);
    }
  };

  // ============================================================================
  // FIREBASE STORAGE - DELETE DOCUMENT
  // ============================================================================
  
  const deleteDocumentFromStorage = async (storagePath) => {
    try {
      if (!storagePath) return;
      
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
      console.log('Document deleted from storage:', storagePath);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // ============================================================================
  // PAYMENT METHOD HELPERS
  // ============================================================================
  
  const getEnabledPaymentMethods = () => {
    return (reservationSettings.paymentMethods || []).filter(method => method && method.enabled === true);
  };

  const getDefaultPaymentMethod = () => {
    const methods = getEnabledPaymentMethods();
    const defaultMethod = methods.find(m => m.isDefault);
    return defaultMethod || methods[0] || null;
  };

  // ============================================================================
  // BIKE AVAILABILITY CHECK
  // ============================================================================
  
  const checkBikeAvailability = async (bikeSelection, startDateTime, endDateTime) => {
    if (!bikeSelection || !startDateTime || !endDateTime) {
      setBikeStatus({ isAvailable: true, message: '' });
      return;
    }

    if (!multiTenantReady) {
      setBikeStatus({ 
        isAvailable: false, 
        message: 'Multi-tenant system not ready - cannot verify availability' 
      });
      return;
    }

    try {
      setCheckingAvailability(true);
      console.log('Checking bike availability for company:', currentCompany.name, 'Bike:', bikeSelection);
      
      const selectedBike = motorbikes.find(bike => 
        bike.regNumber === bikeSelection || 
        bike.id === bikeSelection ||
        (typeof bike === 'string' && bike === bikeSelection)
      );

      if (!selectedBike) {
        setBikeStatus({
          isAvailable: false,
          message: 'Selected bike not found in managed fleet'
        });
        return;
      }

      const bikeIdentifier = selectedBike.regNumber || selectedBike.id || bikeSelection;
      
      if (reservationsCollection) {
        const reservationsQuery = query(
          reservationsCollection,
          where('motorbikeNumber', '==', bikeIdentifier),
          where('availabilityStatus', 'in', ['booked', 'pending'])
        );
        const reservationsSnapshot = await getDocs(reservationsQuery);
        
        let conflictingReservation = null;
        reservationsSnapshot.forEach((doc) => {
          const reservation = doc.data();
          
          if (reservation.companyId && reservation.companyId !== tenantContext?.companyId) {
            console.warn('Cross-company reservation detected:', reservation.companyId);
            return;
          }
          
          if (reservation.availabilityStatus === 'cancel' || reservation.rentalStatus === 'completed') {
            return;
          }
          
          if (editIndex !== null && doc.id === editIndex) {
            return;
          }
          
          const reservationStart = new Date(reservation.pickupDateTime);
          const reservationEnd = new Date(reservation.dropoffDateTime);
          const newStart = new Date(startDateTime);
          const newEnd = new Date(endDateTime);
          
          if (reservationStart <= newEnd && reservationEnd >= newStart) {
            conflictingReservation = {
              startDate: reservation.pickupDateTime.split('T')[0],
              endDate: reservation.dropoffDateTime.split('T')[0],
              status: reservation.availabilityStatus,
              companyName: reservation.companyName || currentCompany.name
            };
          }
        });
        
        if (conflictingReservation) {
          const startDateFormatted = new Date(conflictingReservation.startDate).toLocaleDateString();
          const endDateFormatted = new Date(conflictingReservation.endDate).toLocaleDateString();
          
          setBikeStatus({
            isAvailable: false,
            message: `This bike is already reserved by ${conflictingReservation.companyName} from ${startDateFormatted} to ${endDateFormatted} (Status: ${conflictingReservation.status})`
          });
          return;
        }
      }
      
      setBikeStatus({ isAvailable: true, message: '' });
      
    } catch (error) {
      console.error("Error checking bike availability:", error);
      setBikeStatus({
        isAvailable: true,
        message: 'Unable to check availability. Please verify manually.'
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  // ============================================================================
  // ⭐⭐⭐ ENHANCED SAVE FUNCTION - RESERVATION ONLY (NO INVOICE) ⭐⭐⭐
  // ============================================================================
  
  const handleEnhancedSave = async (e) => {
    e.preventDefault();
    
    console.log('Enhanced save with Firebase Storage, Delivery Charges, and NO license format validation');
    
    if (!validateForm()) {
      alert('Please fix the following errors:\n\n' + formErrors.join('\n'));
      return;
    }

    if (!validateDocuments()) {
      alert('Please provide all required documents before submitting.');
      return;
    }
    
    if (reservationData.availabilityStatus === "cancel") {
      console.log('Processing cancellation...');
      try {
        const enhancedReservationData = {
          ...reservationData,
          cancelledAt: new Date().toISOString(),
          cancelledBy: tenantContext?.userId || currentUser.id,
          cancelledByUsername: tenantContext?.username || currentUser.username,
          companyId: tenantContext?.companyId || currentCompany.id,
          companyName: tenantContext?.companyName || currentCompany.name,
          tenantIsolated: true
        };

        Object.keys(enhancedReservationData).forEach(key => {
          reservationData[key] = enhancedReservationData[key];
        });

        await handleSaveReservation(e);
        alert(`Reservation has been cancelled successfully for ${currentCompany?.name}!`);
        return;
        
      } catch (error) {
        console.error('Error cancelling reservation:', error);
        alert('Error cancelling reservation. Please try again.');
        return;
      }
    }
    
    if (editIndex === null && !bikeStatus.isAvailable && reservationData.motorbikeNumber) {
      alert('This bike is not available for the selected dates. Please choose another bike or different dates.');
      return;
    }
    
    try {
      const selectedBike = motorbikes.find(bike => 
        bike.regNumber === reservationData.motorbikeNumber || 
        bike.id === reservationData.motorbikeNumber ||
        (typeof bike === 'string' && bike === reservationData.motorbikeNumber)
      );

      const bikeModelToSave = selectedBike?.model || selectedModel || reservationData.bikeModel || 'Unknown';
      
      console.log('💾 Saving bike info:', {
        motorbikeNumber: reservationData.motorbikeNumber,
        bikeModel: bikeModelToSave,
        selectedBike: selectedBike
      });

      const selectedAddonData = selectedAddons.map(addonId => {
        const addon = getEnabledAddons().find(a => a.id === addonId);
        if (!addon) return null;
        const cost = addon.pricingMethod === 'perDay' 
          ? (parseFloat(addon.price) * calculationDetails.days)
          : parseFloat(addon.price);
        return {
          id: addon.id,
          name: addon.name,
          category: addon.category,
          price: addon.price,
          pricingMethod: addon.pricingMethod,
          calculatedCost: cost.toFixed(2)
        };
      }).filter(Boolean);

      console.log('Starting document upload process...');
      let uploadedDocumentData = {};
      
      try {
        uploadedDocumentData = await uploadAllDocuments();
        console.log('✅ Documents uploaded successfully:', Object.keys(uploadedDocumentData).length, 'documents');
      } catch (uploadError) {
        console.error('❌ Document upload failed:', uploadError);
        
        const continueWithoutDocs = window.confirm(
          `❌ Document Upload Failed\n\n` +
          `Error: ${uploadError.message}\n\n` +
          `━━━━━━━━━━━━━━━━━━━━━━\n\n` +
          `OPTIONS:\n\n` +
          `✅ Click OK to save reservation WITHOUT documents\n` +
          `   (You can upload documents later by editing the reservation)\n\n` +
          `❌ Click CANCEL to fix the issue and try again\n\n` +
          `━━━━━━━━━━━━━━━━━━━━━━\n\n` +
          `TROUBLESHOOTING:\n` +
          `• Check your internet speed (need at least 0.5 Mbps upload)\n` +
          `• Verify Firebase Storage is enabled in Firebase Console\n` +
          `• Try uploading smaller/fewer documents\n` +
          `• Check browser console (F12) for detailed errors`
        );
        
        if (!continueWithoutDocs) {
          return;
        }
        
        console.log('⚠️  User chose to continue without uploading new documents');
        
        const enabledDocs = getEnabledDocuments();
        enabledDocs.forEach(doc => {
          const file = documentUploads[doc.id];
          if (file && file.isExisting) {
            uploadedDocumentData[doc.id] = {
              documentId: doc.id,
              documentName: doc.name,
              category: doc.category,
              required: doc.required,
              fileName: file.name,
              fileSize: file.size,
              downloadURL: file.downloadURL,
              storagePath: file.storagePath,
              uploadedAt: file.uploadedAt,
              status: 'existing',
              isExisting: true
            };
          }
        });
        
        reservationData.documentUploadFailed = true;
        reservationData.documentUploadError = uploadError.message;
      }

      const discountData = {};
      if (discountState.selectedDiscount || discountState.customDiscountValue) {
        discountData.selectedDiscount = discountState.selectedDiscount;
        discountData.customDiscountType = discountState.customDiscountType;
        discountData.customDiscountValue = discountState.customDiscountValue;
        discountData.reason = discountState.discountReason;
        discountData.amount = discountState.discountAmount;
        discountData.appliedAt = new Date().toISOString();
        discountData.appliedBy = tenantContext?.userId || currentUser.id;
        discountData.appliedByUsername = tenantContext?.username || currentUser.username;
      }

      // ===================================
      // CALCULATE TOTAL WITH DELIVERY CHARGES
      // ===================================
      const deliveryCharges = calculateDeliveryCharges();
      const baseRentalAmount = (reservationData.availabilityStatus === 'booked' && reservationSettings.autoCalculateAmount) 
        ? calculatedAmount 
        : parseFloat(reservationData.rentalAmount) || 0;
      const totalBeforeDiscount = baseRentalAmount + addonCosts + deliveryCharges;
      const discountAmount = discountState.discountAmount || 0;
      const finalTotalAmount = Math.max(0, totalBeforeDiscount - discountAmount);

      // Get location details for saving
      const pickupLocationDetails = getLocationByName(reservationData.pickupLocation);
      const dropoffLocationDetails = getLocationByName(reservationData.dropoffLocation);

      const enhancedReservationData = {
        ...reservationData,
        motorbikeNumber: reservationData.motorbikeNumber,
        bikeModel: bikeModelToSave,
        rentalAmount: baseRentalAmount.toString(),
        deliveryCharges: deliveryCharges.toString(),
        pickupLocationDetails: pickupLocationDetails,
        dropoffLocationDetails: dropoffLocationDetails,
        subtotalAmount: totalBeforeDiscount.toString(),
        discountAmount: discountAmount.toString(),
        totalAmount: finalTotalAmount.toString(),
        selectedAddons: selectedAddonData,
        addonCosts: addonCosts.toString(),
        documentRequirements: getEnabledDocuments(),
        uploadedDocuments: uploadedDocumentData,
        documentValidationStatus: Object.keys(uploadedDocumentData).length >= getRequiredDocuments().length ? 'complete' : 'pending',
        documentsStoredInFirebase: true,
        documentCount: Object.keys(uploadedDocumentData).length,
        discountData: Object.keys(discountData).length > 0 ? discountData : null,
        hasDiscount: discountAmount > 0,
        companyId: tenantContext?.companyId || currentCompany.id,
        companyName: tenantContext?.companyName || currentCompany.name,
        createdBy: tenantContext?.userId || currentUser.id,
        createdByUsername: tenantContext?.username || currentUser.username,
        tenantIsolated: true,
        appliedSettings: {
          calculationMethod: reservationSettings.rentalCalculationMethod,
          minimumDays: reservationSettings.minimumRentalDays,
          autoCalculated: reservationSettings.autoCalculateAmount,
          depositOptions: reservationSettings.depositOptions,
          enabledAddons: getEnabledAddons(),
          enabledDocuments: getEnabledDocuments(),
          enabledPaymentMethods: getEnabledPaymentMethods(),
          enabledLocations: getEnabledLocations(),
          deliveryChargesEnabled: reservationSettings.enableDeliveryCharges,
          fieldSettings: reservationSettings.fieldSettings,
          formBehavior: reservationSettings.formBehavior,
          validationRules: reservationSettings.validationRules,
          discountSettings: reservationSettings.discountSettings,
          licenseValidationRemoved: true,
          noLicenseFormatValidation: true,
          settingsVersion: new Date().toISOString()
        },
        calculationDetails: (reservationData.availabilityStatus === 'booked' && reservationSettings.autoCalculateAmount) 
          ? calculationDetails 
          : null,
        formValidationPassed: true,
        visibleFields: getVisibleFields().length,
        requiredFields: getVisibleFields().filter(([, config]) => config.required).length,
        enhancedFormData: true,
        completeFieldControl: true,
        discountSupport: true,
        deliveryChargesSupport: true,
        licenseFormatValidationDisabled: true,
        noLicenseFormatRequirement: true,
        acceptsAnyLicenseFormat: true,
        pastDatesAllowed: true,
        firebaseStorageEnabled: true,
        bikeModelSaved: true,
        bikeNumberSaved: true
      };

      if (editIndex !== null) {
        enhancedReservationData.addonChanges = addonChanges;
        enhancedReservationData.originalAddons = originalAddons;
        enhancedReservationData.documentChanges = documentChanges;
        enhancedReservationData.originalDocuments = originalDocuments;
        enhancedReservationData.discountChanges = discountState.discountChanges;
        enhancedReservationData.originalDiscount = discountState.originalDiscount;
        enhancedReservationData.updatedAt = new Date().toISOString();
      }

      Object.keys(enhancedReservationData).forEach(key => {
        reservationData[key] = enhancedReservationData[key];
      });

      console.log('💾 ========================================');
      console.log('💾 SAVING RESERVATION WITH DELIVERY CHARGES');
      console.log('💾 ========================================');
      console.log('💾 Bike info:', {
        motorbikeNumber: enhancedReservationData.motorbikeNumber,
        bikeModel: enhancedReservationData.bikeModel
      });
      console.log('💾 Delivery info:', {
        deliveryCharges: enhancedReservationData.deliveryCharges,
        pickupLocation: enhancedReservationData.pickupLocation,
        dropoffLocation: enhancedReservationData.dropoffLocation,
        pickupCharge: pickupLocationDetails?.deliveryCharge || 0,
        dropoffCharge: dropoffLocationDetails?.deliveryCharge || 0
      });
      console.log('💾 Total calculation:', {
        baseRental: baseRentalAmount,
        addons: addonCosts,
        deliveryCharges: deliveryCharges,
        subtotal: totalBeforeDiscount,
        discount: discountAmount,
        finalTotal: finalTotalAmount
      });
      console.log('💾 ========================================');

      await handleSaveReservation(e);
      
      console.log('✅ ========================================');
      console.log('✅ RESERVATION SAVED SUCCESSFULLY');
      console.log('✅ ========================================');
      console.log('✅ Reservation saved with delivery charges!');
      console.log('✅ Invoice can be created from ReservationList');
      console.log('✅ ========================================');
      
      // ============================================================================
      // ✅✅✅ NO AUTOMATIC INVOICE CREATION - USE ReservationList BUTTON ✅✅✅
      // ============================================================================
      // Invoice creation is handled by the "Create Invoice" button in ReservationList
      // This ensures users have full control over when invoices are generated
      
      if (editIndex !== null && (addonChanges || documentChanges || discountState.discountChanges)) {
        console.log('Changes saved successfully');
        setOriginalAddons([...selectedAddons]);
        setOriginalDocuments({ ...documentUploads });
        setDiscountState(prev => ({
          ...prev,
          originalDiscount: prev.selectedDiscount,
          discountChanges: false
        }));
        setAddonChanges(false);
        setDocumentChanges(false);
      }
      
      console.log('✅ Reservation saved successfully to Firebase');
      console.log('✅ User can now create invoice from ReservationList');
      
    } catch (error) {
      console.error('Error in enhanced save:', error);
      alert('Error saving reservation: ' + error.message);
    }
  };

  // ============================================================================
  // RENDER CALCULATION DISPLAY
  // ============================================================================
  
  const renderCalculationDisplay = () => {
    if (!reservationSettings.showCalculationDetails || calculationDetails.days === 0) return null;

    const baseRentalAmount = calculatedAmount;
    const deliveryCharges = calculateDeliveryCharges();
    const totalBeforeDiscount = baseRentalAmount + addonCosts + deliveryCharges;
    const discountAmount = discountState.discountAmount || 0;
    const finalTotal = Math.max(0, totalBeforeDiscount - discountAmount);

    const pickupCharge = getLocationByName(reservationData.pickupLocation) 
      ? parseFloat(getLocationByName(reservationData.pickupLocation).deliveryCharge) || 0 
      : 0;
    const dropoffCharge = getLocationByName(reservationData.dropoffLocation) 
      ? parseFloat(getLocationByName(reservationData.dropoffLocation).deliveryCharge) || 0 
      : 0;

    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0">
            <Calculator size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-white font-medium text-sm sm:text-base">Rental Calculation</h3>
            <p className="text-xs text-gray-400 truncate">
              {calculationDetails.method === 'inclusive' ? 'Calendar days (inclusive)' : 'Duration-based'} • {calculationDetails.days} day{calculationDetails.days !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="bg-gray-700/30 border border-gray-600 rounded-lg p-3 sm:p-4 space-y-3">
          {calculationDetails.selectedBike && (
            <div className="bg-gray-800/50 rounded-lg p-2 sm:p-3">
              <div className="text-xs text-gray-400 mb-1">Selected Bike</div>
              <div className="text-white font-medium text-sm sm:text-base break-words">
                {calculationDetails.selectedBike.model} ({calculationDetails.selectedBike.regNumber})
              </div>
              <div className="text-sm text-gray-300 mt-1">
                ${calculationDetails.selectedBike.dailyRate}/day
              </div>
            </div>
          )}

          {calculationDetails.breakdown && (
            <div className="text-xs sm:text-sm space-y-1">
              <div className="flex justify-between text-gray-300 gap-2">
                <span className="truncate">Calculation Method:</span>
                <span className="font-medium text-white flex-shrink-0">{calculationDetails.breakdown.calculation}</span>
              </div>
              {calculationDetails.breakdown.explanation && (
                <div className="text-xs text-gray-400 bg-gray-800/50 p-2 rounded break-words">
                  {calculationDetails.breakdown.explanation}
                </div>
              )}
              {calculationDetails.breakdown.minimumApplied && (
                <div className="text-xs text-orange-400 bg-orange-900/20 p-2 rounded border border-orange-700 break-words">
                  ⚠️ Minimum rental of {reservationSettings.minimumRentalDays} day{reservationSettings.minimumRentalDays !== 1 ? 's' : ''} applied
                </div>
              )}
            </div>
          )}

          <div className="border-t border-gray-600 pt-3 space-y-2">
            <div className="flex justify-between text-xs sm:text-sm gap-2">
              <span className="text-gray-300">Base Rental ({calculationDetails.days} day{calculationDetails.days !== 1 ? 's' : ''}):</span>
              <span className="font-medium text-white flex-shrink-0">${baseRentalAmount.toFixed(2)}</span>
            </div>

            {selectedAddons.length > 0 && (
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-gray-300">Add-ons ({selectedAddons.length}):</span>
                <span className="font-medium text-blue-400 flex-shrink-0">+${addonCosts.toFixed(2)}</span>
              </div>
            )}

            {deliveryCharges > 0 && (
              <>
                <div className="flex justify-between text-xs sm:text-sm gap-2">
                  <span className="text-gray-300">Delivery Charges:</span>
                  <span className="font-medium text-purple-400 flex-shrink-0">+${deliveryCharges.toFixed(2)}</span>
                </div>
                
                <div className="ml-4 space-y-1 text-xs">
                  {pickupCharge > 0 && (
                    <div className="flex justify-between text-gray-400 gap-2">
                      <span className="flex items-center gap-1">
                        <MapPin size={10} className="text-green-400" />
                        <span>Pickup ({reservationData.pickupLocation}):</span>
                      </span>
                      <span className="text-green-400">+${pickupCharge.toFixed(2)}</span>
                    </div>
                  )}
                  {dropoffCharge > 0 && (
                    <div className="flex justify-between text-gray-400 gap-2">
                      <span className="flex items-center gap-1">
                        <MapPin size={10} className="text-blue-400" />
                        <span>Drop-off ({reservationData.dropoffLocation}):</span>
                      </span>
                      <span className="text-blue-400">+${dropoffCharge.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-between text-xs sm:text-sm font-medium pt-2 border-t border-gray-600 gap-2">
              <span className="text-gray-300">Subtotal:</span>
              <span className="text-white flex-shrink-0">${totalBeforeDiscount.toFixed(2)}</span>
            </div>

            {discountAmount > 0 && (
              <div className="flex justify-between text-xs sm:text-sm gap-2">
                <span className="text-gray-300">Discount:</span>
                <span className="font-medium text-green-400 flex-shrink-0">-${discountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between text-base sm:text-lg font-bold pt-2 border-t-2 border-orange-500 gap-2">
              <span className="text-orange-400">Total Amount:</span>
              <span className="text-orange-400 flex-shrink-0">${finalTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // USE EFFECTS
  // ============================================================================
  
  // Check camera support and Firebase Storage
  useEffect(() => {
    setCameraSupported(
      navigator.mediaDevices && 
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );
    
    try {
      if (!storage) {
        console.error('Firebase Storage is not initialized');
        setStorageAvailable(false);
      } else {
        console.log('✓ Firebase Storage is available');
        setStorageAvailable(true);
      }
    } catch (error) {
      console.error('Firebase Storage check failed:', error);
      setStorageAvailable(false);
    }
  }, []);

  // Initialize from existing reservation data when editing
  useEffect(() => {
    if (editIndex !== null && reservationData && !settingsLoading) {
      console.log('🔄 Initializing edit mode with reservation data:', reservationData);
      
      if (reservationData.motorbikeNumber) {
        const selectedBike = motorbikes.find(bike => 
          bike.regNumber === reservationData.motorbikeNumber || 
          bike.id === reservationData.motorbikeNumber
        );
        if (selectedBike && selectedBike.model) {
          setSelectedModel(selectedBike.model);
          console.log('✅ Bike model loaded:', selectedBike.model);
        }
      }
      
      if (reservationData.selectedAddons && Array.isArray(reservationData.selectedAddons)) {
        console.log('📦 Loading add-ons from reservation:', reservationData.selectedAddons);
        
        const addonIds = reservationData.selectedAddons.map(addon => {
          if (typeof addon === 'object' && addon.id) {
            return addon.id;
          }
          return addon;
        }).filter(id => id != null);
        
        console.log('📦 Extracted addon IDs:', addonIds);
        
        setSelectedAddons(addonIds);
        setOriginalAddons([...addonIds]);
        console.log('✅ Add-ons loaded:', addonIds.length, 'add-ons');
        
        if (reservationData.addonCosts) {
          const costs = parseFloat(reservationData.addonCosts);
          setAddonCosts(costs);
          console.log('✅ Add-on costs loaded:', costs);
        }
      } else {
        console.log('⚠️ No add-ons found in reservation data');
        setSelectedAddons([]);
        setOriginalAddons([]);
      }
      
      console.log('📄 ========================================');
      console.log('📄 DOCUMENT LOADING SECTION');
      console.log('📄 ========================================');
      console.log('📄 reservationData.uploadedDocuments exists?', !!reservationData.uploadedDocuments);
      
      if (reservationData.uploadedDocuments) {
        console.log('📄 ✅ uploadedDocuments field EXISTS!');
        
        const docUploads = {};
        let processedCount = 0;
        
        Object.entries(reservationData.uploadedDocuments).forEach(([docId, docData]) => {
          if (!docData) {
            console.log(`📄 ❌ Document ${docId} is null/undefined - SKIPPING`);
            return;
          }
          
          const hasFileName = docData.fileName || docData.originalFileName || docData.name || docData.documentName;
          
          if (hasFileName) {
            const numericDocId = typeof docId === 'string' ? parseInt(docId, 10) : docId;
            
            const fileName = docData.fileName || docData.originalFileName || docData.name || docData.documentName || `Document ${numericDocId}`;
            const fileSize = docData.fileSize || docData.size || 0;
            const fileType = docData.fileType || docData.type || 'application/octet-stream';
            const downloadURL = docData.downloadURL || '';
            const storagePath = docData.storagePath || '';
            const uploadedAt = docData.uploadedAt || new Date().toISOString();
            
            docUploads[numericDocId] = {
              name: fileName,
              size: fileSize,
              type: fileType,
              isExisting: true,
              isNew: false,
              uploadedAt: uploadedAt,
              downloadURL: downloadURL,
              storagePath: storagePath
            };
            
            processedCount++;
            console.log(`📄 ✅ Document ${numericDocId} ADDED to docUploads`);
          }
        });
        
        console.log('📄 PROCESSING COMPLETE:', processedCount, 'documents');
        
        setDocumentUploads(docUploads);
        setOriginalDocuments({ ...docUploads });
        console.log('📄 ✅ setDocumentUploads CALLED!');
      } else {
        console.log('📄 ❌ NO uploadedDocuments field in reservationData!');
        setDocumentUploads({});
        setOriginalDocuments({});
      }

      if (reservationData.discountData) {
        console.log('💰 Loading discount data:', reservationData.discountData);
        const discountData = reservationData.discountData;
        
        setDiscountState(prev => ({
          ...prev,
          selectedDiscount: discountData.selectedDiscount || null,
          customDiscountType: discountData.customDiscountType || 'percentage',
          customDiscountValue: discountData.customDiscountValue || '',
          discountReason: discountData.reason || '',
          discountAmount: parseFloat(discountData.amount) || 0,
          originalDiscount: discountData.selectedDiscount || null,
          discountChanges: false
        }));
        console.log('✅ Discount data loaded');
      } else {
        console.log('⚠️ No discount data found');
      }

      if (reservationSettings?.fieldSettings) {
        const customFields = {};
        Object.entries(reservationSettings.fieldSettings).forEach(([fieldKey, fieldConfig]) => {
          if (fieldConfig.isCustom) {
            customFields[fieldKey] = reservationData[fieldKey] || '';
          }
        });
        setOriginalCustomFields(customFields);
        setCustomFieldChanges({});
        console.log('✅ Custom fields loaded:', Object.keys(customFields).length, 'fields');
      }
      
      console.log('✅ ========================================');
      console.log('✅ Edit mode initialization complete!');
      console.log('✅ ========================================');
    }
  }, [editIndex, reservationData, motorbikes, reservationSettings, settingsLoading]);

  // Update addon costs when selection or calculation details change
  useEffect(() => {
    const costs = calculateAddonCosts(selectedAddons, calculationDetails.days);
    setAddonCosts(costs);
  }, [selectedAddons, calculationDetails.days, reservationSettings.rentalAddons]);

  // Update discount amount when base amount changes
  useEffect(() => {
    const deliveryCharges = calculateDeliveryCharges();
    const baseAmount = calculatedAmount + addonCosts + deliveryCharges;
    
    if (discountState.selectedDiscount) {
      const newDiscountAmount = calculateDiscountAmount(baseAmount, discountState.selectedDiscount.type, discountState.selectedDiscount.value);
      setDiscountState(prev => ({
        ...prev,
        discountAmount: newDiscountAmount
      }));
    } else if (discountState.customDiscountValue) {
      const newDiscountAmount = calculateDiscountAmount(baseAmount, discountState.customDiscountType, discountState.customDiscountValue);
      setDiscountState(prev => ({
        ...prev,
        discountAmount: newDiscountAmount
      }));
    }
  }, [calculatedAmount, addonCosts, discountState.selectedDiscount, discountState.customDiscountType, discountState.customDiscountValue, reservationData.pickupLocation, reservationData.dropoffLocation]);

  // Load settings on mount
  useEffect(() => {
    if (currentCompany && currentUser && companyId) {
      loadReservationSettings();
    }
  }, [currentCompany, currentUser, companyId]);

  // Effect to recalculate rental amount when inputs or settings change
  useEffect(() => {
    if (!settingsLoading && reservationSettings.autoCalculateAmount) {
      const amount = calculateRentalAmount(
        reservationData.motorbikeNumber,
        reservationData.pickupDateTime,
        reservationData.dropoffDateTime
      );
      
      if (amount > 0 && reservationData.availabilityStatus === 'booked') {
        if (handleInputChange) {
          handleInputChange({
            target: {
              name: 'rentalAmount',
              value: amount.toString()
            }
          });
        }
      }
    }
  }, [
    reservationData.motorbikeNumber, 
    reservationData.pickupDateTime, 
    reservationData.dropoffDateTime, 
    reservationData.availabilityStatus,
    reservationSettings.autoCalculateAmount,
    reservationSettings.rentalCalculationMethod,
    reservationSettings.minimumRentalDays,
    settingsLoading
  ]);

  // Apply default values from settings when creating new reservation
  useEffect(() => {
    if (!editIndex && !settingsLoading && reservationSettings.depositOptions?.length > 0) {
      const defaultUpdates = {};
      
      const defaultDeposit = reservationSettings.depositOptions.find(d => d.isDefault);
      if (defaultDeposit && (!reservationData.depositAmount || reservationData.depositAmount === '$100')) {
        defaultUpdates.depositAmount = defaultDeposit.label;
      }
      
      const paymentMethods = getEnabledPaymentMethods();
      if (paymentMethods.length > 0 && !reservationData.paymentMethod) {
        const defaultPaymentMethod = getDefaultPaymentMethod();
        if (defaultPaymentMethod) {
          defaultUpdates.paymentMethod = defaultPaymentMethod.name;
        }
      }
      
      const fieldSettings = reservationSettings.fieldSettings || {};
      if (fieldSettings.pickupLocation?.defaultValue && !reservationData.pickupLocation) {
        defaultUpdates.pickupLocation = fieldSettings.pickupLocation.defaultValue;
      }
      if (fieldSettings.dropoffLocation?.defaultValue && !reservationData.dropoffLocation) {
        defaultUpdates.dropoffLocation = fieldSettings.dropoffLocation.defaultValue;
      }
      
      if (Object.keys(defaultUpdates).length > 0) {
        Object.keys(defaultUpdates).forEach(key => {
          if (handleInputChange) {
            handleInputChange({
              target: {
                name: key,
                value: defaultUpdates[key]
              }
            });
          }
        });
      }
    }
  }, [settingsLoading, reservationSettings, editIndex]);

  // Multi-tenant initialization
  useEffect(() => {
    const initializeMultiTenantForm = () => {
      const warnings = [];
      
      console.log('Initializing enhanced reservation form with Firebase Storage and Delivery Locations...');
      
      if (!currentUser || !currentCompany) {
        warnings.push('Missing authentication context');
        setMultiTenantReady(false);
        setSecurityWarnings(warnings);
        return;
      }

      if (currentUser.companyId !== currentCompany.id) {
        warnings.push('User-company relationship mismatch detected');
        setMultiTenantReady(false);
        setSecurityWarnings(warnings);
        return;
      }

      if (!dataIsolationActive) {
        warnings.push('Data isolation not active');
      }

      if (!reservationsCollection) {
        warnings.push('Reservations collection not properly initialized');
      }

      if (editIndex !== null && reservationData.companyId) {
        if (!validateCompanyAccess || !validateCompanyAccess(reservationData.companyId)) {
          warnings.push('Cannot edit reservation - belongs to different company');
          setMultiTenantReady(false);
          setSecurityWarnings(warnings);
          return;
        }
      }

      setSecurityWarnings(warnings);
      setMultiTenantReady(true);
      console.log('Enhanced reservation form with Firebase Storage and Delivery Locations initialized');
    };

    initializeMultiTenantForm();
  }, [currentUser, currentCompany, dataIsolationActive, editIndex, reservationData.companyId, validateCompanyAccess, reservationsCollection, motorbikes.length]);

  // Effects for availability checking
  useEffect(() => {
    if (reservationData.motorbikeNumber && reservationData.pickupDateTime && reservationData.dropoffDateTime && multiTenantReady) {
      if (editIndex === null) {
        checkBikeAvailability(
          reservationData.motorbikeNumber,
          reservationData.pickupDateTime,
          reservationData.dropoffDateTime
        );
      } else {
        setBikeStatus({ isAvailable: true, message: '' });
      }
    }
  }, [reservationData.motorbikeNumber, reservationData.pickupDateTime, reservationData.dropoffDateTime, multiTenantReady, editIndex]);

  useEffect(() => {
    setAvailableBikes(motorbikes);
  }, [motorbikes]);

  // ============================================================================
  // ACCESS DENIED STATE
  // ============================================================================
  
  if (!multiTenantReady && currentUser && currentCompany) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 sm:p-8 w-full max-w-md border border-red-700">
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-red-400 mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">Access Restricted</h3>
            <p className="text-sm sm:text-base text-gray-300 mb-4">
              Multi-tenant security validation failed. You may not have permission to access this reservation form.
            </p>
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4">
              <div className="text-red-300 text-xs sm:text-sm space-y-1">
                {securityWarnings.map((warning, index) => (
                  <div key={index} className="flex items-start gap-2 break-words text-left">
                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={handleClosePopup}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-2 sm:p-4">
      <div className={`bg-gray-800 rounded-xl shadow-xl w-full max-h-[98vh] sm:max-h-[95vh] overflow-y-auto border border-gray-700 ${
        reservationSettings.formBehavior?.compactMode ? 'sm:max-w-4xl' : 'sm:max-w-5xl'
      }`}>
        
        <div className="p-4 sm:p-6">
          
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
            <div className="flex flex-col w-full sm:w-auto">
              <div className="flex items-center justify-between w-full sm:w-auto">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
                  <MotorbikeIcon size={20} className="text-orange-500 mr-2 flex-shrink-0" />
                  <span className="truncate">{editIndex !== null ? 'Edit Reservation' : 'Add Reservation'}</span>
                </h3>
                
                <button 
                  onClick={handleClosePopup} 
                  className="sm:hidden text-gray-400 hover:text-white p-2"
                  disabled={isSaving || uploadingDocuments}
                >
                  <X size={24} />
                </button>
              </div>
              
            </div>
            
            <button 
              onClick={handleClosePopup} 
              className="hidden sm:block text-gray-400 hover:text-white flex-shrink-0"
              disabled={isSaving || uploadingDocuments}
            >
              <X size={24} />
            </button>
          </div>

          {/* FORM VALIDATION SUMMARY */}
          {formErrors.length > 0 && (
            <div className="mb-4 sm:mb-6 bg-red-900/30 border border-red-700 rounded-lg p-3 sm:p-4">
              <div className="flex items-center text-red-400 font-medium mb-2 text-sm sm:text-base">
                <AlertCircle className="mr-2 flex-shrink-0" size={16} />
                <span>Form Validation Issues ({formErrors.length})</span>
              </div>
              <div className="space-y-1 text-red-300 text-xs sm:text-sm">
                {formErrors.slice(0, 5).map((error, index) => (
                  <div key={index} className="flex items-start gap-2 break-words">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-1 flex-shrink-0"></span>
                    <span>{error}</span>
                  </div>
                ))}
                {formErrors.length > 5 && (
                  <div className="text-red-400 text-xs">
                    And {formErrors.length - 5} more issues...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MAIN FORM */}
          <form onSubmit={handleEnhancedSave} className="space-y-4 sm:space-y-6">
            
            <CustomerInformationSection
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              reservationSettings={reservationSettings}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
            />

            <RentalDetailsSection
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              reservationSettings={reservationSettings}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              motorbikes={motorbikes}
              bikeStatus={bikeStatus}
              checkingAvailability={checkingAvailability}
              editIndex={editIndex}
              handleInputChange={handleInputChange}
            />

            <DepositSelectionSection
              reservationSettings={reservationSettings}
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
            />

            <BookingStatusSection
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              reservationSettings={reservationSettings}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
            />

            <CustomFieldsSection
              reservationSettings={reservationSettings}
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
              editIndex={editIndex}
              fieldChanges={customFieldChanges}
            />

            <RentalAddonsSection
              reservationSettings={reservationSettings}
              selectedAddons={selectedAddons}
              setSelectedAddons={setSelectedAddons}
              addonCosts={addonCosts}
              calculationDetails={calculationDetails}
              editIndex={editIndex}
              addonChanges={addonChanges}
              setAddonChanges={setAddonChanges}
              originalAddons={originalAddons}
            />

            <DiscountSection
              reservationSettings={reservationSettings}
              discountState={discountState}
              setDiscountState={setDiscountState}
              calculatedAmount={calculatedAmount}
              addonCosts={addonCosts}
              editIndex={editIndex}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
            />

            <PaymentMethodSection
              reservationSettings={reservationSettings}
              reservationData={reservationData}
              handleEnhancedInputChange={handleEnhancedInputChange}
              fieldValidation={fieldValidation}
              isSaving={isSaving}
              uploadingDocuments={uploadingDocuments}
            />

            <DocumentRequirementsSection
              reservationSettings={reservationSettings}
              documentUploads={documentUploads}
              setDocumentUploads={setDocumentUploads}
              documentValidation={documentValidation}
              setDocumentValidation={setDocumentValidation}
              uploadProgress={uploadProgress}
              capturedImages={capturedImages}
              setCapturedImages={setCapturedImages}
              cameraStream={cameraStream}
              setCameraStream={setCameraStream}
              activeCameraDoc={activeCameraDoc}
              setActiveCameraDoc={setActiveCameraDoc}
              cameraSupported={cameraSupported}
              uploadingDocuments={uploadingDocuments}
              storageAvailable={storageAvailable}
              editIndex={editIndex}
              documentChanges={documentChanges}
              setDocumentChanges={setDocumentChanges}
              originalDocuments={originalDocuments}
            />

            {renderCalculationDisplay()}

            {/* ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={handleClosePopup}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2.5 sm:py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={isSaving || uploadingDocuments}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2.5 sm:py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                disabled={isSaving || uploadingDocuments || !multiTenantReady}
              >
                {(isSaving || uploadingDocuments) ? (
                  <>
                    <Loader className="animate-spin flex-shrink-0" size={18} />
                    <span className="truncate">{uploadingDocuments ? 'Uploading...' : 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <Save size={18} className="flex-shrink-0" />
                    <span className="truncate">{editIndex !== null ? 'Update Reservation' : 'Add Reservation'}</span>
                  </>
                )}
              </button>
            </div>

            {!multiTenantReady && (
              <div className="text-center text-orange-400 text-xs sm:text-sm bg-orange-900/20 p-2 sm:p-3 rounded-lg border border-orange-700 break-words">
                <AlertTriangle className="inline mr-2 flex-shrink-0" size={14} />
                <span>Form is initializing security context...</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default SimplifiedReservationForm;